import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Tag, X, MapPin, Loader2, CheckCircle, Percent, Ticket, Truck, ChevronRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import ProductCard from '../../components/common/ProductCard';
import FreeDeliveryCelebration, { useFreeDeliveryUnlock } from '../../components/common/FreeDeliveryCelebration';
import FreeGiftCelebration, { useGiftUnlock } from '../../components/common/FreeGiftCelebration';
import RemoveConfirmModal from '../../components/common/RemoveConfirmModal';
import { cldImg } from '../../utils/cloudinary';

// Popup listing all active coupons — tapping a coupon applies it to the cart
function CartCouponPopup({ coupons, cartTotal, bestPrice, appliedCode, applyingCode, onApply, onClose }) {
  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ zIndex: 9999 }} onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <p className="font-bold text-brand-dark text-base">Available coupons</p>
            {bestPrice && (
              <p className="text-teal-600 font-bold text-sm mt-0.5">
                Pay as low as <span className="text-lg">₹{bestPrice.toLocaleString('en-IN')}</span> *
              </p>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors flex-shrink-0">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Coupon list */}
        <div className="px-4 pb-5 space-y-3 max-h-[60vh] overflow-y-auto">
          {coupons.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">No active coupons available right now.</p>
          )}
          {coupons.map(coupon => {
            const isFlat = coupon.type === 'flat';
            const isPct = coupon.type === 'percentage';
            const discountText = isFlat ? `Get ₹${Math.round(coupon.value)} Off` : isPct ? `Get ${Math.round(coupon.value)}% Off` : 'Free Shipping';
            const minText = coupon.min_order > 0 ? `on order above ₹${Math.round(coupon.min_order)}` : '';
            const eligible = cartTotal >= (coupon.min_order || 0);
            const isApplied = appliedCode === coupon.code;
            const isApplying = applyingCode === coupon.code;
            return (
              <div key={coupon.id} className={`border border-dashed rounded-xl overflow-hidden ${isApplied ? 'border-green-300 bg-green-50/40' : 'border-gray-200'}`}>
                {/* Top row */}
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand-yellow flex items-center justify-center flex-shrink-0">
                      {isFlat || isPct ? <Percent className="w-4 h-4 text-brand-dark" /> : <Truck className="w-4 h-4 text-brand-dark" />}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-brand-dark">{discountText}</p>
                      {minText && <p className="text-xs text-gray-400">{minText}</p>}
                    </div>
                  </div>
                </div>
                {/* Code + apply row */}
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-dashed border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-2">
                    <Ticket className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm font-bold text-brand-dark tracking-widest">{coupon.code}</span>
                  </div>
                  {isApplied ? (
                    <span className="text-xs font-bold text-green-600">✓ Applied</span>
                  ) : (
                    <button onClick={() => onApply(coupon.code)} disabled={isApplying}
                      className="px-3 py-1.5 bg-brand-yellow text-white text-xs font-bold rounded-lg hover:bg-brand-yellow-dark transition-colors disabled:opacity-60">
                      {isApplying ? '...' : eligible ? 'Apply' : 'Apply'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {bestPrice && (
            <p className="text-xs text-gray-400 text-center pt-1">* After applying the highest eligible coupon</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function CartPage() {
  const { cart, cartTotal, cartHasFreeShipping, freeTiers, freeShippingAbove, updateQty, removeFromCart, clearCart, addToCart } = useCart();
  const { toggleWishlist } = useWishlist();
  const [removeTarget, setRemoveTarget] = useState(null);
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponShortfall, setCouponShortfall] = useState(null);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [couponPopupOpen, setCouponPopupOpen] = useState(false);
  const [applyingCode, setApplyingCode] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [recommendations, setRecommendations] = useState([]);

  // Pincode + shipping rates
  const [pincode, setPincode] = useState('');
  const [ratesLoading, setRatesLoading] = useState(false);
  const [rates, setRates] = useState(null); // null = not checked yet
  const [ratesError, setRatesError] = useState(null); // 'api' | 'unserviceable' | null
  const [selectedRate, setSelectedRate] = useState(null);
  const [fallbackShipping, setFallbackShipping] = useState(null);
  const [fallbackShippingEnabled, setFallbackShippingEnabled] = useState(true);
  const pincodeRef = useRef();

  const fetchRates = async (pin) => {
    if (!/^\d{6}$/.test(pin)) return;
    setRatesLoading(true);
    setRates(null);
    setRatesError(null);
    setSelectedRate(null);
    try {
      const totalWeight = cart.reduce((sum, item) => sum + (item.weight || 500) * item.qty, 0) || 500;
      const { data } = await api.get('/shipping/rates', { params: { pincode: pin, weight: totalWeight } });
      if (data.success && data.rates?.length > 0) {
        setRates(data.rates);
        setSelectedRate(data.rates[0]);
      } else {
        setRates([]);
        setRatesError('unserviceable');
      }
    } catch {
      setRates([]);
      setRatesError('api'); // API issue — not a serviceability problem
    } finally {
      setRatesLoading(false);
    }
  };

  const discount = (() => {
    if (!coupon) return 0;
    const val = Number(coupon.value) || 0;
    let d = 0;
    if (coupon.type === 'percentage') {
      d = Math.round((cartTotal * val) / 100);
      if (coupon.max_discount != null) d = Math.min(d, Number(coupon.max_discount));
    } else if (coupon.type === 'flat') {
      d = Math.round(val);
    }
    // free_shipping handles itself via shipping logic; never discount more than the cart
    return Math.min(d, cartTotal);
  })();

  const fallbackFee = fallbackShippingEnabled && fallbackShipping != null ? fallbackShipping : null;
  const baseShippingFee = selectedRate?.rate ?? fallbackFee;
  const orderAmt = cartTotal - discount;
  const isFreeShipping = cartHasFreeShipping || (freeShippingAbove && freeShippingAbove > 0 && orderAmt >= freeShippingAbove);
  const [celebrate, closeCelebration] = useFreeDeliveryUnlock(orderAmt, freeShippingAbove);
  const [celebrateGift, closeGiftCelebration] = useGiftUnlock(cartTotal, freeTiers);
  const shippingFee = isFreeShipping ? 0 : baseShippingFee;
  const grandTotal = shippingFee !== null ? cartTotal - discount + shippingFee : null;

  const handleApplyCoupon = async (codeArg) => {
    const code = (typeof codeArg === 'string' ? codeArg : couponCode).trim();
    if (!code) return;
    setCouponLoading(true);
    setApplyingCode(code);
    try {
      const { data } = await api.post('/coupons/validate', {
        code,
        order_total: cartTotal,
      });
      setCoupon(data.coupon);
      setCouponCode(code);
      setCouponPopupOpen(false);
      toast.success('Coupon applied!');
    } catch (err) {
      const res = err.response?.data;
      // Minimum-order not met → show a centered modal telling how much more to add
      if (res?.shortfall != null) {
        setCouponPopupOpen(false);
        setCouponShortfall({ code: code.toUpperCase(), shortfall: res.shortfall, min_order: res.min_order });
      } else {
        toast.error(res?.message || 'Invalid coupon');
      }
    } finally {
      setCouponLoading(false);
      setApplyingCode(null);
    }
  };

  useEffect(() => {
    api.get('/settings/public')
      .then(({ data }) => {
        const s = data.settings || {};
        const charge = s.shipping_charge !== '' && s.shipping_charge != null ? parseFloat(s.shipping_charge) : null;
        setFallbackShipping(charge);
        const enabled = !(s.fallback_shipping_enabled === '0' || s.fallback_shipping_enabled === 'false' || s.fallback_shipping_enabled === false);
        setFallbackShippingEnabled(enabled);
      })
      .catch(() => {
        setFallbackShipping(null);
        setFallbackShippingEnabled(true);
      });
  }, []);

  useEffect(() => {
    if (cart.length === 0) { setRecommendations([]); return; }
    const productId = cart[0].id;
    api.get(`/products/${productId}/relations`)
      .then(({ data }) => {
        const items = data.you_may_also_like || data.recommendation || [];
        if (items.length > 0) {
          setRecommendations(items);
        } else {
          // fallback: load recent products excluding cart items
          const cartIds = cart.map(c => c.product_id);
          api.get('/products', { params: { limit: 8 } }).then(r => {
            const filtered = (r.data.products || []).filter(p => !cartIds.includes(p.id));
            setRecommendations(filtered.slice(0, 6));
          }).catch(() => {});
        }
      })
      .catch(() => {});
  }, [cart.map(c => c.id).join(',')]);

  useEffect(() => {
    api.get('/coupons/active').then(({ data }) => setActiveCoupons(data.coupons || [])).catch(() => {});
  }, []);

  const handleRemoveCoupon = () => {
    setCoupon(null);
    setCouponCode('');
  };

  // Best achievable price after the highest eligible coupon (for the offers teaser)
  const couponBestPrice = (() => {
    if (activeCoupons.length === 0) return null;
    let bestSaving = 0;
    activeCoupons.forEach(c => {
      if (cartTotal < (c.min_order || 0)) return;
      if (c.type === 'flat') bestSaving = Math.max(bestSaving, Number(c.value));
      else if (c.type === 'percentage') bestSaving = Math.max(bestSaving, Math.round(cartTotal * Number(c.value) / 100));
    });
    return bestSaving > 0 ? Math.round(cartTotal - bestSaving) : null;
  })();

  const handleRemove = async (key) => {
    setRemovingId(key);
    await new Promise(r => setTimeout(r, 300));
    removeFromCart(key);
    setRemovingId(null);
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-4 py-16">
        <div className="w-24 h-24 bg-brand-yellow-light rounded-full flex items-center justify-center mb-6">
          <ShoppingBag size={40} className="text-brand-yellow" />
        </div>
        <h2 className="text-2xl font-bold text-brand-dark mb-2">Your cart is empty</h2>
        <p className="text-gray-500 mb-8 max-w-xs">Looks like you haven't added anything yet. Start shopping and find something you love!</p>
        <Link to="/products" className="btn-primary px-8 py-3 rounded-xl font-semibold text-base">
          Shop Now
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 md:py-10">
      {celebrate && <FreeDeliveryCelebration onClose={closeCelebration} />}
      {celebrateGift && <FreeGiftCelebration gift={celebrateGift} onClose={closeGiftCelebration} />}
      {couponPopupOpen && (
        <CartCouponPopup
          coupons={activeCoupons}
          cartTotal={cartTotal}
          bestPrice={couponBestPrice}
          appliedCode={coupon?.code}
          applyingCode={applyingCode}
          onApply={handleApplyCoupon}
          onClose={() => setCouponPopupOpen(false)}
        />
      )}
      {removeTarget && (
        <RemoveConfirmModal
          item={removeTarget}
          onClose={() => setRemoveTarget(null)}
          onRemove={() => { removeFromCart(removeTarget.key); setRemoveTarget(null); }}
          onMoveToWishlist={() => {
            toggleWishlist(removeTarget.product_id);
            removeFromCart(removeTarget.key);
            setRemoveTarget(null);
          }}
        />
      )}

      {/* Coupon minimum-order modal */}
      {couponShortfall && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
          onClick={() => setCouponShortfall(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-brand-yellow/20 flex items-center justify-center">
              <Tag className="w-7 h-7 text-brand-yellow" />
            </div>
            <h3 className="text-lg font-bold text-brand-dark mb-1">Almost there!</h3>
            <p className="text-sm text-gray-600">
              Add <span className="font-bold text-brand-dark">₹{couponShortfall.shortfall.toLocaleString('en-IN')}</span> more
              to avail coupon <span className="font-semibold text-brand-dark">{couponShortfall.code}</span>.
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Minimum order ₹{couponShortfall.min_order?.toLocaleString('en-IN')} required.
            </p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setCouponShortfall(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                Close
              </button>
              <button onClick={() => {
                  setCouponShortfall(null);
                  document.getElementById('cart-recommendations')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex-1 py-2.5 rounded-xl bg-brand-yellow text-white text-sm font-semibold hover:bg-brand-yellow-dark">
                Add More
              </button>
            </div>
          </div>
        </div>
      )}
      <h1 className="text-2xl font-bold text-brand-dark mb-4">
        Shopping Cart <span className="text-gray-400 font-normal text-base ml-2">({cart.length} {cart.length === 1 ? 'item' : 'items'})</span>
      </h1>

      {/* Progress bar — free shipping + gift tiers */}
      {(freeTiers?.length > 0 || (freeShippingAbove && freeShippingAbove > 0)) && (() => {
        const giftStations = (freeTiers || []).map(t => ({
          type: 'gift',
          amount: parseFloat(t.min_amount),
          label: t.product?.name?.split(' ').slice(0, 2).join(' '),
          img: t.product?.images?.[0]?.image_url,
        }));
        const shippingStation = freeShippingAbove && freeShippingAbove > 0
          ? [{ type: 'shipping', amount: freeShippingAbove, label: 'Free Delivery', img: null }]
          : [];
        const allStations = [...giftStations, ...shippingStation].sort((a, b) => a.amount - b.amount);
        if (allStations.length === 0) return null;

        const DOT = 52;
        const HALF = DOT / 2;
        const n = allStations.length;

        const nextStation = allStations.find(s => cartTotal < s.amount);
        const lastUnlocked = [...allStations].reverse().find(s => cartTotal >= s.amount);

        // Evenly space dots; compute fill to match
        const lastIdx = lastUnlocked ? allStations.indexOf(lastUnlocked) : -1;
        const nextIdx = nextStation ? allStations.indexOf(nextStation) : n;
        let fillPct = 0;
        if (lastIdx >= 0) {
          const prevRatio = n === 1 ? 1 : lastIdx / (n - 1);
          const nextRatio = nextIdx < n ? (n === 1 ? 1 : nextIdx / (n - 1)) : 1;
          const prevAmt = allStations[lastIdx].amount;
          const nextAmt = nextIdx < n ? allStations[nextIdx].amount : allStations[lastIdx].amount;
          const segFrac = nextAmt > prevAmt ? Math.min(1, (cartTotal - prevAmt) / (nextAmt - prevAmt)) : 1;
          fillPct = Math.min(100, (prevRatio + segFrac * (nextRatio - prevRatio)) * 100);
        }

        return (
          <div className="rounded-xl px-3 py-3 mb-6" style={{ backgroundColor: '#fdf6e3', border: '1px solid #e8d9b0' }}>
            {lastUnlocked && (
              <p className="text-xs font-bold mb-1" style={{ color: '#92400e' }}>
                {lastUnlocked.type === 'shipping' ? '🚚' : '🎁'}{' '}
                <span className="underline">{lastUnlocked.label}</span> unlocked!
                {nextStation && (
                  <span className="font-normal" style={{ color: '#78350f' }}>
                    {' '}Add ₹{(nextStation.amount - cartTotal).toLocaleString('en-IN')} more for{' '}
                    {nextStation.type === 'shipping' ? 'Free Delivery' : nextStation.label}
                  </span>
                )}
              </p>
            )}
            {!lastUnlocked && nextStation && (
              <p className="text-xs font-semibold mb-1" style={{ color: '#78350f' }}>
                {nextStation.type === 'shipping' ? '🚚' : '🎁'}{' '}
                Add ₹{(nextStation.amount - cartTotal).toLocaleString('en-IN')} more to get{' '}
                <span className="font-bold" style={{ color: '#92400e' }}>{nextStation.type === 'shipping' ? 'Free Delivery' : nextStation.label}</span>
              </p>
            )}
            <div className="relative mt-3" style={{ paddingLeft: HALF, paddingRight: HALF, paddingTop: 16, paddingBottom: 44 }}>
              <div className="relative w-full rounded-full" style={{ height: 8, marginTop: HALF - 4, backgroundColor: '#e5d8b0' }}>
                <div className="absolute left-0 top-0 h-full rounded-full transition-all duration-500" style={{ width: `${fillPct}%`, backgroundColor: '#F5B400' }} />
              </div>
              {allStations.map((s, i) => {
                const ratio = n === 1 ? 1 : i / (n - 1);
                const isActive = lastUnlocked && s.amount === lastUnlocked.amount;
                const isPassed = cartTotal >= s.amount && !isActive;
                return (
                  <div key={i} className="absolute flex flex-col items-center"
                    style={{ left: `calc(${HALF}px + ${ratio * 100}% * (100% - ${DOT}px) / 100%)`, transform: 'translateX(-50%)', top: 0, width: 64 }}>
                    <span className="text-[9px] font-bold whitespace-nowrap mb-1" style={{ color: isActive || isPassed ? '#92400e' : '#a78040' }}>
                      ₹{s.amount.toLocaleString('en-IN')}
                    </span>
                    <div className="rounded-full flex items-center justify-center transition-all overflow-hidden"
                      style={{
                        width: DOT, height: DOT,
                        border: isActive ? '3px solid #F5B400' : isPassed ? '3px solid #F5B400' : '2px solid #d4b96a',
                        backgroundColor: '#fff',
                        boxShadow: isActive ? '0 2px 10px #F5B40055' : 'none',
                        opacity: isActive || isPassed ? 1 : 0.75,
                      }}>
                      {s.type === 'shipping'
                        ? <span className="text-2xl">🚚</span>
                        : s.img
                          ? <img src={cldImg(s.img, 160)} alt={s.label} className="w-full h-full object-cover" />
                          : <span className="text-xl">🎁</span>}
                    </div>
                    <span className="text-[8px] font-bold text-center leading-tight mt-1" style={{ maxWidth: 64, display: 'block', color: isActive || isPassed ? '#111' : '#7a6030', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Cart Items */}
        <div className="flex-1 space-y-4">
          {cart.map((item) => {
            const key = item.key;
            const isRemoving = removingId === key;
            const imageUrl = item.image || null;

            // Free gift item — special card
            if (item.isFreeGift) {
              return (
                <div key={key} className="bg-orange-50 border border-orange-200 rounded-2xl p-4 flex gap-4 items-center">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-white border border-orange-200 shrink-0">
                    {imageUrl
                      ? <img src={cldImg(imageUrl, 160)} alt={item.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-2xl">🎁</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 text-sm line-clamp-2">{item.name}</p>
                    {item.variant_label && <p className="text-xs text-gray-500 mt-0.5">{item.variant_label}</p>}
                    <span className="inline-block mt-1.5 text-[10px] font-bold bg-orange-400 text-white px-2 py-0.5 rounded-full">
                      🎁 FREE GIFT
                    </span>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="font-bold text-orange-500 text-base">FREE</span>
                    {item.mrp > 0 && (
                      <span className="block text-xs text-gray-400 line-through">₹{item.mrp.toLocaleString('en-IN')}</span>
                    )}
                  </div>
                </div>
              );
            }

            return (
              <div
                key={key}
                className={`bg-white rounded-2xl border border-gray-100 p-4 flex gap-4 transition-all duration-300 ${isRemoving ? 'opacity-0 scale-95' : 'opacity-100'}`}
              >
                {/* Image */}
                <Link to={`/product/${item.slug}`} className="flex-shrink-0">
                  <div className="w-24 h-24 md:w-28 md:h-28 rounded-xl overflow-hidden bg-gray-50">
                    {imageUrl ? (
                      <img src={cldImg(imageUrl, 160)} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl">🛍️</div>
                    )}
                  </div>
                </Link>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.slug}`} className="font-semibold text-brand-dark text-sm md:text-base line-clamp-2 hover:text-brand-yellow transition-colors">
                    {item.name}
                  </Link>

                  {/* Variant info */}
                  {item.variant_label && (
                    <div className="mt-1">
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                        {item.variant_label}
                      </span>
                    </div>
                  )}

                  {/* Price */}
                  <div className="flex items-center gap-2 mt-2">
                    {item.qty > 1 ? (
                      <>
                        <span className="font-bold text-brand-dark">₹{(item.price * item.qty).toLocaleString('en-IN')}</span>
                        <span className="text-xs text-gray-400">₹{item.price?.toLocaleString('en-IN')} × {item.qty}</span>
                      </>
                    ) : (
                      <span className="font-bold text-brand-dark">₹{item.price?.toLocaleString('en-IN')}</span>
                    )}
                  </div>

                  {/* Qty + Remove */}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => item.qty === 1 ? setRemoveTarget(item) : updateQty(key, item.qty - 1)}
                        className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-10 text-center text-sm font-semibold text-brand-dark">{item.qty}</span>
                      <button
                        onClick={() => updateQty(key, item.qty + 1)}
                        className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
                      >
                        <Plus size={14} />
                      </button>
                    </div>

                    <button
                      onClick={() => setRemoveTarget(item)}
                      className="text-gray-400 hover:text-brand-red transition-colors p-2"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Subtotal (desktop) */}
                <div className="hidden md:flex flex-col items-end justify-between">
                  <span className="font-bold text-brand-dark text-lg">
                    ₹{(item.price * item.qty).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Clear cart */}
          <button
            onClick={() => { clearCart(); toast.success('Cart cleared'); }}
            className="text-sm text-gray-400 hover:text-brand-red transition-colors mt-2"
          >
            Clear cart
          </button>
        </div>

        {/* Order Summary */}
        <div className="lg:w-96">
          {/* Available coupons teaser — opens popup */}
          {activeCoupons.length > 0 && (
            <button onClick={() => setCouponPopupOpen(true)}
              className="w-full mb-4 flex items-center justify-between bg-white border border-gray-200 rounded-2xl px-4 py-3 hover:border-brand-yellow hover:bg-brand-yellow/5 transition-all group">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-brand-yellow flex items-center justify-center flex-shrink-0">
                  <Percent className="w-4 h-4 text-brand-dark" />
                </div>
                <div className="text-left">
                  {couponBestPrice ? (
                    <p className="text-sm font-bold text-teal-600">Get it for ₹{couponBestPrice.toLocaleString('en-IN')} <span className="text-xs text-gray-400 font-normal">*</span></p>
                  ) : (
                    <p className="text-sm font-bold text-brand-dark">Available coupons</p>
                  )}
                  <p className="text-xs text-gray-400">with {activeCoupons.length} offer{activeCoupons.length > 1 ? 's' : ''}</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-brand-dark transition-colors" />
            </button>
          )}

          <div className="bg-white rounded-2xl border border-gray-100 p-6 sticky top-24">
            <h2 className="text-lg font-bold text-brand-dark mb-5">Order Summary</h2>

            {/* Coupon */}
            <div className="mb-5">
              {coupon ? (
                <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Tag size={16} className="text-green-600" />
                    <span className="text-sm font-semibold text-green-700">{coupon.code}</span>
                    <span className="text-xs text-green-600">- ₹{discount.toLocaleString('en-IN')}</span>
                  </div>
                  <button onClick={handleRemoveCoupon} className="text-green-600 hover:text-green-800">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={e => setCouponCode(e.target.value.toUpperCase())}
                    onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
                    placeholder="Coupon code"
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-brand-yellow"
                  />
                  <button
                    onClick={handleApplyCoupon}
                    disabled={couponLoading}
                    className="px-4 py-2.5 bg-brand-yellow text-white text-sm font-semibold rounded-xl hover:bg-brand-yellow-dark transition-colors disabled:opacity-60"
                  >
                    {couponLoading ? '...' : 'Apply'}
                  </button>
                </div>
              )}
            </div>

            {/* Pincode + Shipping rates */}
            <div className="mb-2">
              <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Delivery Pincode</p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={pincodeRef}
                    type="tel"
                    maxLength={6}
                    value={pincode}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, '');
                      setPincode(v);
                      if (v.length === 6) fetchRates(v);
                    }}
                    placeholder="Enter 6-digit pincode"
                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-brand-yellow"
                  />
                </div>
                {ratesLoading && <div className="flex items-center px-2"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>}
              </div>

              {/* Courier options — hide when free shipping unlocked */}
              {rates !== null && rates.length > 0 && !isFreeShipping && (
                <div className="mt-2 space-y-1.5">
                  {rates.map(r => (
                    <button key={r.id} onClick={() => setSelectedRate(r)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm transition-all
                        ${selectedRate?.id === r.id ? 'border-brand-yellow bg-brand-yellow-light' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
                      <div className="flex items-center gap-2">
                        {selectedRate?.id === r.id && <CheckCircle className="w-4 h-4 text-brand-yellow flex-shrink-0" />}
                        <p className="font-semibold text-gray-800">Shipping Charge</p>
                      </div>
                      <span className="font-bold text-gray-800">₹{r.rate}</span>
                    </button>
                  ))}
                </div>
              )}
              {ratesError === 'unserviceable' && (
                <p className={`mt-2 text-xs ${fallbackShippingEnabled ? 'text-gray-400' : 'text-red-500'}`}>
                  {fallbackShippingEnabled
                    ? 'Delivery not available to this pincode; fallback shipping charge will be used.'
                    : 'Delivery not available to this pincode.'}
                </p>
              )}
              {ratesError === 'api' && (
                <p className={`mt-2 text-xs ${fallbackShippingEnabled ? 'text-gray-400' : 'text-red-500'}`}>
                  {fallbackShippingEnabled
                    ? 'Unable to fetch rates; fallback shipping charge will be used.'
                    : 'Unable to fetch shipping rates. Shipping will be calculated at checkout.'}
                </p>
              )}
            </div>

            {/* Totals */}
            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>₹{cartTotal.toLocaleString('en-IN')}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>
                    Coupon discount
                    {coupon?.type === 'percentage' && (
                      <span className="ml-1 text-xs font-semibold">({Math.round(Number(coupon.value))}% off)</span>
                    )}
                  </span>
                  <span>- ₹{discount.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600">
                <span>Shipping</span>
                <span className="font-medium">
                  {shippingFee === null
                    ? fallbackFee !== null
                      ? <span className="text-gray-500 text-xs">Default shipping charge will apply</span>
                      : <span className="text-gray-400 text-xs">Enter pincode</span>
                    : shippingFee === 0 ? <span className="text-green-600 font-semibold">FREE</span>
                    : `₹${shippingFee}`}
                </span>
              </div>
              <div className="border-t border-gray-100 pt-3 flex justify-between font-bold text-brand-dark text-base">
                <span>Total</span>
                <span>{grandTotal === null ? <span className="text-gray-400 text-sm font-normal">Enter pincode for total</span> : `₹${grandTotal.toLocaleString('en-IN')}`}</span>
              </div>
            </div>

            {/* Savings badge */}
            {discount > 0 && (
              <div className="mt-4 bg-green-50 rounded-xl px-4 py-2 text-center">
                <p className="text-green-700 text-sm font-semibold">
                  You're saving ₹{discount.toLocaleString('en-IN')} on this order!
                </p>
              </div>
            )}

            <button
              onClick={() => {
                navigate('/checkout', { state: { coupon, discount, shippingFee: shippingFee ?? 0, grandTotal: grandTotal ?? cartTotal - discount, selectedCourier: selectedRate, pincode } });
              }}
              className="mt-6 w-full btn-primary py-4 rounded-xl font-bold text-base flex items-center justify-center gap-2"
            >
              Proceed to Checkout
              <ArrowRight size={18} />
            </button>

            <Link to="/products" className="mt-3 block text-center text-sm text-gray-500 hover:text-brand-yellow transition-colors">
              Continue Shopping
            </Link>

            {/* Trust badges */}
            <div className="mt-6 grid grid-cols-3 gap-2 text-center">
              {['🔒 Secure', '🚚 Fast Ship', '✅ Quality Assured'].map(badge => (
                <div key={badge} className="bg-gray-50 rounded-lg py-2 px-1 text-xs text-gray-500">{badge}</div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* You May Also Like */}
      {recommendations.length > 0 && (
        <div className="mt-12" id="cart-recommendations">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-xs font-bold text-brand-yellow uppercase tracking-widest mb-0.5">Handpicked for You</p>
              <h2 className="text-xl font-bold text-brand-dark">You May Also Like</h2>
            </div>
            <Link to="/products" className="text-sm text-gray-400 hover:text-brand-yellow transition-colors">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {recommendations.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
